import { useCallback, useEffect, useRef, useState } from 'react';
import { X, LinkIcon, Maximize2, Copy, Check, Sparkles, Star, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useRouter } from '@tanstack/react-router';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';

import { useConversation, useConversationRaw, useToggleStarred } from '@/hooks/use-conversations';
import { useTenants } from '@/hooks/use-tenants';
import { buildLogfireTraceUrl, buildGrafanaTraceUrl, extractThreadId, detectLogfireProject } from '@/lib/utils/format';
import { ConversationView } from './ConversationView';
import { toEnvelope } from '@/lib/conversation-envelope';

interface ResizableConversationPreviewProps {
  conversationId: string;
  tenantId?: string;
  onPreviewClose?: () => void;
}

const MIN_WIDTH_PERCENT = 30;
const MAX_WIDTH_PERCENT = 50;
const STORAGE_KEY = 'conversation-preview-width';
const DEFAULT_WIDTH = 33.33;

// Get initial width from localStorage or use default
const getInitialWidth = (): number => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = parseFloat(stored);
      if (!isNaN(parsed) && parsed >= MIN_WIDTH_PERCENT && parsed <= MAX_WIDTH_PERCENT) {
        return parsed;
      }
    }
  } catch {
    // Ignore localStorage errors
  }
  return DEFAULT_WIDTH;
};

/** Stable reference: an inline array would re-run the viewer's effect
 *  on every render. The preview pane is narrow, so it opens with fewer
 *  tracks than the full-page views.
 */
const PREVIEW_TRACKS: readonly string[] = ['tool'];

export const ResizableConversationPreview = ({
  conversationId,
  tenantId: propTenantId,
  onPreviewClose,
}: ResizableConversationPreviewProps) => {
  const router = useRouter();
  const [width, setWidth] = useState<number>(getInitialWidth);
  const [isResizing, setIsResizing] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  const [copiedJson, setCopiedJson] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);

  const { data: conversation } = useConversation(conversationId, propTenantId || null);
  const tenantId = conversation?.tenant_id ?? propTenantId ?? null;
  const { data: rawConversation, isLoading: isLoadingRaw, error: rawError } = useConversationRaw(conversationId, tenantId);
  const { data: tenants } = useTenants();
  const toggleStarMutation = useToggleStarred();

  // Get tenant name
  const tenant = tenants?.find(t => t.id === conversation?.tenant_id);
  const tenantName = tenant?.company || (conversation?.tenant_id ? conversation.tenant_id.slice(0, 8) : undefined);

  // Get category (prefer manual category over AI category)
  const displayCategory = conversation?.category || conversation?.ai_category;

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = width;
    },
    [width]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !containerRef.current) return;

      const container = containerRef.current.parentElement;
      if (!container) return;

      const containerWidth = container.offsetWidth;
      const deltaX = startXRef.current - e.clientX; // Inverted because we're resizing from the left
      const deltaPercent = (deltaX / containerWidth) * 100;
      const newWidth = Math.max(MIN_WIDTH_PERCENT, Math.min(MAX_WIDTH_PERCENT, startWidthRef.current + deltaPercent));

      setWidth(newWidth);

      // Save to localStorage
      try {
        localStorage.setItem(STORAGE_KEY, newWidth.toString());
      } catch {
        // Ignore localStorage errors
      }
    },
    [isResizing]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const handleCopyURL = () => {
    if (!tenantId) {
      toast.error('Tenant ID not available');
      return;
    }
    const url = `${window.location.origin}/agent/conversations/${tenantId}/${conversationId}`;
    void navigator.clipboard
      .writeText(url)
      .then(() => {
        toast.info('Conversation URL copied to clipboard');
      })
      .catch((e: unknown) => {
        console.log(e);
      });
  };

  const handleOpenFullScreen = () => {
    // Close preview and navigate to full conversation view
    if (!tenantId) {
      toast.error('Tenant ID not available');
      return;
    }
    onPreviewClose?.();
    void router.navigate({
      to: '/agent/conversations/$tenantId/$conversationId',
      params: { tenantId, conversationId },
    });
  };

  const handleCopyJson = () => {
    if (!rawConversation) return;
    void navigator.clipboard
      .writeText(JSON.stringify(rawConversation, null, 2))
      .then(() => {
        setCopiedJson(true);
        setTimeout(() => { setCopiedJson(false); }, 2000);
        toast.success('Raw JSON copied');
      })
      .catch((err: unknown) => {
        console.error('Failed to copy:', err);
        toast.error('Failed to copy JSON');
      });
  };

  const handleToggleStar = () => {
    if (!conversation || !tenantId) return;
    void toggleStarMutation.mutateAsync({
      conversationId: conversation.id,
      tenantId: tenantId,
    });
  };

  return (
    <>
      {/* Resize handle - spans the entire gap */}
      <div
        className={cn(
          'group relative z-10 flex h-full items-center justify-center',
          'cursor-col-resize transition-colors',
          'hover:bg-primary/10',
          isResizing && 'bg-primary/10'
        )}
        onMouseDown={handleMouseDown}
        style={{ width: '1rem' }}
      />

      {/* Preview panel */}
      <div
        ref={containerRef}
        className="h-full"
        style={{
          width: `${width}%`,
        }}
      >
        <div className="flex h-full flex-col rounded-md border bg-background pb-4">
          {!conversation ? (
            // Loading skeleton
            <div className="flex flex-col h-full p-4">
              <Skeleton className="h-8 w-3/4 mb-4" />
              <Skeleton className="h-4 w-1/2 mb-2" />
              <Skeleton className="h-4 w-2/3 mb-4" />
              <div className="flex-1 flex items-center justify-center">
                <div className="border-primary h-12 w-12 animate-spin rounded-full border-b-2"></div>
              </div>
            </div>
          ) : (
            // Loaded content
            <>
              <div className="shrink-0 border-b p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span
                      className="cursor-pointer hover:opacity-70 transition-opacity shrink-0"
                      onClick={handleToggleStar}
                    >
                      <Star
                        className={`h-4 w-4 ${conversation.is_starred ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`}
                      />
                    </span>
                    <h3 className="text-base font-bold text-foreground line-clamp-1">
                      {conversation.title || conversationId.slice(0, 8)}
                    </h3>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" onClick={handleOpenFullScreen} className="h-7 w-7">
                          <Maximize2 className="h-3 w-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Full screen</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" onClick={handleCopyURL} className="h-7 w-7">
                          <LinkIcon className="h-3 w-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Copy URL</TooltipContent>
                    </Tooltip>
                    {isLoadingRaw ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center justify-center h-7 w-7">
                            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>Loading trace data...</TooltipContent>
                      </Tooltip>
                    ) : rawConversation?.turns[0] ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <a
                            href={buildLogfireTraceUrl(
                              rawConversation.turns[0].trace_id,
                              detectLogfireProject(rawConversation.agent_version),
                              {
                                since: rawConversation.started_at,
                                until: rawConversation.ended_at,
                              },
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors hover:bg-accent hover:text-accent-foreground"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </TooltipTrigger>
                        <TooltipContent>View in Logfire</TooltipContent>
                      </Tooltip>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center justify-center h-7 w-7 text-muted-foreground/40">
                            <ExternalLink className="h-3 w-3" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>No trace data</TooltipContent>
                      </Tooltip>
                    )}
                    {(() => {
                      const threadId = extractThreadId('source_identifier' in conversation ? conversation.source_identifier : undefined);
                      if (!threadId) return null;
                      return (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <a
                              href={buildGrafanaTraceUrl(threadId, {
                                since: conversation.conversation_date,
                                until: rawConversation?.ended_at,
                              })}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors hover:bg-accent hover:text-accent-foreground"
                            >
                              <ExternalLink className="h-3 w-3 text-orange-500" />
                            </a>
                          </TooltipTrigger>
                          <TooltipContent>View in Grafana</TooltipContent>
                        </Tooltip>
                      );
                    })()}
                    <span
                      className="cursor-pointer text-muted-foreground hover:text-foreground ml-1"
                      onClick={onPreviewClose}
                    >
                      <X className="h-4 w-4" />
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  <span>{conversation.turn_count} messages</span>
                  {tenantName && (
                    <>
                      <span>•</span>
                      <span>{tenantName}</span>
                    </>
                  )}
                  {conversation.initiated_by_employee_name && (
                    <>
                      <span>•</span>
                      <span>{conversation.initiated_by_employee_name}</span>
                    </>
                  )}
                  {displayCategory && (
                    <>
                      <span>•</span>
                      <span>{displayCategory}</span>
                    </>
                  )}
                  {conversation.app_name && (
                    <>
                      <span>•</span>
                      <span className="inline-block px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-[10px]">
                        {conversation.app_name}
                      </span>
                    </>
                  )}
                </div>
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-1 flex-col overflow-hidden">
                <div className="flex items-center justify-between gap-2 px-4 pt-3">
                  <TabsList className="h-8">
                    <TabsTrigger value="chat" className="text-xs">Chat</TabsTrigger>
                    <TabsTrigger value="details" className="text-xs">Details</TabsTrigger>
                    <TabsTrigger value="raw" className="text-xs">Raw</TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value="chat" className="flex-[1_1_0] overflow-y-scroll px-4 pt-3 pb-6 space-y-4">
                  <ConversationView
                    data={toEnvelope(conversation, rawConversation)}
                    show={PREVIEW_TRACKS}
                  />
                </TabsContent>

                <TabsContent value="details" className="flex-[1_1_0] overflow-y-scroll px-4 pt-3 pb-6 space-y-3">
                  {/* Categories */}
                  <div className="rounded border bg-card p-3 space-y-2">
                    <div className="text-xs font-semibold">Categories</div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Manual:</span>
                        <span>{conversation.category || '-'} {conversation.subcategory && `/ ${conversation.subcategory}`}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Sparkles className="w-3 h-3 text-purple-500" />
                          AI:
                        </span>
                        <span className="text-purple-700">
                          {conversation.ai_category || '-'} {conversation.ai_subcategory && `/ ${conversation.ai_subcategory}`}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="rounded border bg-card p-3 space-y-2">
                    <div className="text-xs font-semibold">Details</div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Date:</span>
                        <span>{new Date(conversation.conversation_date).toLocaleDateString()}</span>
                      </div>
                      {conversation.workflow_types && conversation.workflow_types.length > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Workflows:</span>
                          <span className="text-right">{conversation.workflow_types.join(', ')}</span>
                        </div>
                      )}
                      {conversation.initiated_by_employee_name && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Initiated By:</span>
                          <span>{conversation.initiated_by_employee_name}</span>
                        </div>
                      )}
                      {'source' in conversation && conversation.source && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Source:</span>
                          <span className="font-semibold capitalize">{conversation.source}</span>
                        </div>
                      )}
                      {'source_identifier' in conversation && conversation.source_identifier && (() => {
                        const sourceId = conversation.source_identifier;
                        return (
                          <div className="flex justify-between items-center gap-2">
                            <span className="text-muted-foreground">Source ID:</span>
                            <div className="flex items-center gap-1 flex-1 min-w-0 justify-end">
                              <span className="font-mono text-[10px] truncate" title={sourceId}>
                                {sourceId}
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  void navigator.clipboard.writeText(sourceId).then(() => {
                                    toast.success('Source ID copied');
                                  });
                                }}
                                className="shrink-0 p-0.5 hover:bg-muted rounded"
                              >
                                <Copy className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="raw" className="flex-[1_1_0] overflow-y-scroll px-4 pt-3 pb-6">
                  <div className="rounded border bg-card p-3 relative">
                    {!isLoadingRaw && !rawError && (
                      <Button
                        variant="outline"
                        size="icon"
                        className="absolute top-2 right-2 h-6 w-6"
                        onClick={handleCopyJson}
                        disabled={!rawConversation}
                      >
                        {copiedJson ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                      </Button>
                    )}
                    <pre className="text-xs overflow-auto pr-10">
                      {isLoadingRaw ? (
                        <div className="flex items-center justify-center py-8 text-muted-foreground">
                          Loading raw data...
                        </div>
                      ) : rawError ? (
                        <div className="text-destructive">
                          Error loading raw data: {(rawError).message}
                        </div>
                      ) : rawConversation ? (
                        JSON.stringify(rawConversation, null, 2)
                      ) : (
                        <div className="text-muted-foreground">No raw data available</div>
                      )}
                    </pre>
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </div>
    </>
  );
};
